import React from "react";
import { commitMutation, fetchQuery, graphql } from "react-relay";

import { faLightbulbOn } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faCheckCircle, faCheckSquare } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";
import cx from "classnames";
import { type DOMOutputSpec, Node as ProseNode, ResolvedPos } from "prosemirror-model";
import { EditorState, NodeSelection, Plugin, TextSelection, Transaction } from "prosemirror-state";
import { Decoration, EditorView, type NodeView } from "prosemirror-view";
import { el, setAttr } from "redom";

import type { JiraColorName } from "~/account/Integrations/Jira/JiraIntegration";
import type { AssigneeCompletionData } from "~/actionItems/ActionItemCompletionMenu";
import { NoteBlockStatus, StatusCondition } from "~/actionItems/enum";
import { LinkIcon, type LinkIconData } from "~/common/ActionItemLinkageModal";
import type { AttendeeAttrs } from "~/common/Attendees";
import { type NoteCheckboxData, NoteCheckboxDOMComponent } from "~/common/NoteCheckbox";
import { activeIntegrationLinks } from "~/integrations/actionItemIntegration";
import { selectionToJiraText } from "~/integrations/actionItemIntegration/utils";
import { ReactComponent as JiraLogo } from "~/legacy-assets/images/logos/jira.svg";
import { cloneNode, type DOMComponent, getElementByClassName, getHTMLElement, render, setStyles } from "~/lib/dom";
import { hasOwnProperty } from "~/lib/js";
import { AssigneeTrigger, BlockActionMenu, type BlockActionMenuData, DueDateTrigger } from "~/lib/prose/blockActions";
import { setBlockType } from "~/lib/prose/commands";
import { type Keybinding, NodeExtension } from "~/lib/prose/extension";
import { blockActionsEnabled } from "~/lib/prose/plugins/blockActions";
import { getContext } from "~/lib/prose/plugins/context";
import { blockTypeShortcut } from "~/lib/prose/plugins/inputRules";
import { promptResponseAttrs } from "~/lib/prose/plugins/prompt";
import type { getPosHandler } from "~/lib/prose/types";
import { blurView, expandSelectionToBlocks, findBlockAndPos } from "~/lib/prose/utils";
import { clearTooltip, setTooltip } from "~/lib/utils";
import { isAcceptFlowFFOn } from "~/meetingRecorder/utils";
import { environment } from "~/models";
import {
	type AssigneeStatusType,
	getActionItemMenu,
	getCustomSectionTips,
	getItemsForProse,
} from "~/notes/AssignableDropdown";
import { ProseLinkedItemText, type ProseLinkedItemTextData } from "~/notes/LinkedItemText";
import { Modal } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";
import toast, { type ToastId } from "~/ui/toast";

import type { assignableSuggestionTooltipQuery } from "~/__generated__/assignableSuggestionTooltipQuery.graphql";
import type { assignableToggleFieldDoneMutation as MutationType } from "~/__generated__/assignableToggleFieldDoneMutation.graphql";

import stylesitem from "~/actionItems/ActionItemActions.module.css";
import goalCheckboxStyles from "~/common/NoteCheckbox/GoalCheckbox.module.css";
import noteCheckboxStyles from "~/common/NoteCheckbox/NoteCheckbox.module.css";

import styles from "../../css/assignable.module.css";
import assignableStyles from "./css/Assignable.module.css";

class Guidance implements DOMComponent<string> {
	el: HTMLElement;
	private suggestionPromise?: Promise<string | null | undefined>;

	static template = el(
		"div",
		{ class: styles.suggestion, ...setTooltip("View Guidance", { delayed: true }) },
		render(<FontAwesomeIcon icon={faLightbulbOn} fixedWidth />),
	);

	static suggestionTooltipQuery = graphql`
		query assignableSuggestionTooltipQuery($id: ID!) {
			employeeGuidance(topicId: $id) {
				promptManager
				promptReport
			}
		}
	`;

	constructor(
		public guidanceId: string,
		public showManager: boolean,
	) {
		this.el = cloneNode(Guidance.template);
		this.el.onclick = this.onSuggestionClick;
	}

	update(newGuidanceId: string) {
		if (newGuidanceId !== this.guidanceId) delete this.suggestionPromise;
		this.guidanceId = newGuidanceId;
	}

	/**
	 * Tries to load the guidance data.
	 *
	 * If it's successful, the resulting data is saved for the next time the
	 * modal is opened. If it fails, the next call will try again.
	 */
	private ensureSuggestionLoaded(): Promise<string | null | undefined> {
		if (!this.suggestionPromise) {
			this.suggestionPromise = fetchQuery<assignableSuggestionTooltipQuery>(
				environment,
				Guidance.suggestionTooltipQuery,
				{
					id: this.guidanceId,
				},
			)
				.toPromise()
				.then(res => {
					const text = this.showManager ? res?.employeeGuidance?.promptManager : res?.employeeGuidance?.promptReport;
					return text;
				})
				.catch(e => {
					Sentry.captureException(e);
					// Retry next time
					this.suggestionPromise = undefined;
					return undefined;
				});
		}

		return this.suggestionPromise;
	}

	private onSuggestionClick = () => {
		const openModal = (tooltip: React.ReactElement) => {
			__globalModalContext?.open(({ close }) => (
				<Modal onClose={close} aria-label="Discussion Guidance" className={styles.suggestionModal}>
					<Modal.Header onClose={close}>
						<Modal.Title>
							<FontAwesomeIcon icon={faLightbulbOn} fixedWidth className={styles.lightBulbOn} /> Discussion Guidance
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<p>{tooltip}</p>
					</Modal.Body>
				</Modal>
			));
		};

		this.ensureSuggestionLoaded().then(text => text && openModal(<span>{text}</span>));
	};
}

interface JiraStatusLinkData {
	statusName: string;
	statusColor: string;
	href: string;
}

class JiraStatusLink implements DOMComponent<JiraStatusLinkData> {
	el: HTMLAnchorElement;
	private data?: JiraStatusLinkData;

	static template = el("a", {
		contenteditable: "false",
		target: "_blank",
		rel: "noopener noreferrer",
		class: assignableStyles.statusIndicator,
	});

	constructor(data: JiraStatusLinkData) {
		this.el = cloneNode(JiraStatusLink.template);
		this.update(data);
	}

	update(data: JiraStatusLinkData) {
		if (data.statusColor !== this.data?.statusColor) {
			if (this.data) {
				// Not the first render, remove whichever class was added the last time
				this.el.classList.remove(assignableStyles[this.data.statusColor as JiraColorName]);
			}
			this.el.classList.add(assignableStyles[data.statusColor as JiraColorName]);
		}

		if (data.statusName !== this.data?.statusName) {
			this.el.innerText = data.statusName.toUpperCase();
		}

		if (data.href !== this.data?.href) {
			this.el.href = data.href;
		}

		this.data = data;
	}
}

interface JiraMetadataData {
	key: string;
	statusName: string;
	statusColor: string;
	view: EditorView;
}

class JiraMetadata implements DOMComponent<JiraMetadataData> {
	el: HTMLElement;
	private link: HTMLAnchorElement;
	private data?: JiraMetadataData;
	private href?: string;
	private statusLink?: JiraStatusLink;

	static template = el(
		"div",
		{ contenteditable: "false", class: assignableStyles.jiraLinkContainer },
		render(<JiraLogo className={assignableStyles.jiraLinkIcon} />),
		" ",
		el("a", {
			contenteditable: "false",
			target: "_blank",
			rel: "noopener noreferrer",
			class: assignableStyles.jiraLinkBubble,
		}),
		// The JiraStatusLink goes here, before the " " text node.
		" ",
	);

	constructor(data: JiraMetadataData) {
		this.el = cloneNode(JiraMetadata.template);
		const link = getElementByClassName(this.el, assignableStyles.jiraLinkBubble);
		if (!(link instanceof HTMLAnchorElement)) throw new Error("Invalid template");
		this.link = link;
		this.update(data);
	}

	update(data: JiraMetadataData) {
		const newKey = JiraMetadata.getTrimmedKey(data);
		if (!this.data || newKey !== JiraMetadata.getTrimmedKey(this.data)) {
			this.link.innerText = newKey;
		}

		if (data.key !== this.data?.key) {
			if (this.data) clearTooltip(this.link);
			const tooltipAttrs = setTooltip(data.key, "top center");
			setAttr(this.link, tooltipAttrs);
			if (tooltipAttrs.style) setStyles(this.link, tooltipAttrs.style);
		}

		const context = getContext(data.view.state);
		const baseUrl = context.jira?.baseUrl ?? "";
		const href = `${baseUrl}/browse/${data.key}`;
		if (href !== this.href) {
			this.href = this.link.href = href;
		}

		const { statusName, statusColor } = data;
		if (!this.statusLink && statusName && statusColor) {
			this.statusLink = new JiraStatusLink({ statusName, statusColor, href });
			// Put it after the link but before the " " text node (to match previous
			// behaviour)
			this.link.insertAdjacentElement("afterend", this.statusLink.el);
		} else if (this.statusLink) {
			if (statusName && statusColor) {
				this.statusLink.update({ statusName, statusColor, href });
			} else {
				this.statusLink.el.remove();
				delete this.statusLink;
			}
		}

		this.data = data;
	}

	private static getTrimmedKey(data: JiraMetadataData): string {
		const [projectKey, issueKey] = data.key.split("-");
		return projectKey.length > 5 ? `${projectKey.slice(0, 4)}…-${issueKey}` : data.key;
	}
}

interface AssignableData {
	node: ProseNode;
	view: EditorView;
	options: { [k: string]: unknown };
	decorations: readonly Decoration[];
	getPos: boolean | (() => number);
}

class AssignableComponent implements DOMComponent<AssignableData> {
	el: HTMLElement;
	// Store the contentDOM (publicly) on the class so it can be accessed by the NodeView
	contentDOM: HTMLElement;
	private checkboxContainer: HTMLElement;
	private assignableBlock: HTMLElement;
	private assignableContent: HTMLElement;
	private assignableInnerWrapper: HTMLElement;
	private pmcontent: HTMLElement;
	private checkbox: NoteCheckboxDOMComponent;
	private linkedText?: ProseLinkedItemText;
	private data?: AssignableData;
	private linkIcon?: LinkIcon;
	private jiraMetadata?: JiraMetadata;
	private guidance?: Guidance;
	private blockActionMenu?: BlockActionMenu;
	private dueDateTrigger?: DueDateTrigger;
	private assigneeTrigger: AssigneeTrigger;

	static template = el(
		"div",
		{ class: "react-node-view", pmblock: "" },
		el(
			"div",
			{ pmcontentwrapper: "" },
			el(
				"div",
				{
					class: cx(stylesitem.actionItemActions__parent, "assignable-block copy"),
				},
				el("div", { class: styles.checkboxContainer, contenteditable: "false" }),
				el(
					"div",
					{ class: "assignable-content" },
					el(
						"div",
						{ class: "assignable-inner-wrapper" },
						el("div", { pmcontent: "" }, el("div", { reactnodeviewcontent: "", class: "react-node-view-content" })),
					),
				),
			),
		),
	);

	constructor(data: AssignableData) {
		this.el = cloneNode(AssignableComponent.template);

		this.assigneeTrigger = new AssigneeTrigger({
			node: data.node,
			view: data.view,
			viewerId: data.options.viewerId as string,
			isPublic: Boolean(data.options.isPublic),
		});

		const context = getContext(data.view.state);
		const hideDueDateTrigger = context?.isCustomSection;

		if (data.node.type.name === "todo" && !hideDueDateTrigger) {
			this.dueDateTrigger = new DueDateTrigger({
				node: data.node,
				view: data.view,
			});
		}

		this.assignableContent = getHTMLElement(this.el, ".assignable-content");
		this.assignableInnerWrapper = getHTMLElement(this.el, ".assignable-inner-wrapper");
		this.pmcontent = getHTMLElement(this.el, "[pmcontent]");
		const checkboxContainer = getElementByClassName(this.el, styles.checkboxContainer);
		if (!(checkboxContainer instanceof HTMLElement)) throw new Error("Invalid template");
		this.checkboxContainer = checkboxContainer;
		this.contentDOM = getHTMLElement(this.el, "[reactnodeviewcontent]");

		const { node } = data;
		this.el.classList.add(`react-node-view-${node.type.name}`);
		this.assignableBlock = getHTMLElement(this.el, ".assignable-block");
		this.assignableBlock.dataset.blockId = node.attrs.blockId;
		this.assignableBlock.dataset.blockType = node.type.name;

		this.checkbox = new NoteCheckboxDOMComponent(this.getCheckboxData(data));
		this.checkboxContainer.append(this.checkbox.el);

		this.update(data);
	}

	deinit() {
		this.blockActionMenu?.deinit();
		this.linkIcon?.deinit();
		this.dueDateTrigger?.deinit();
		this.assigneeTrigger?.deinit();
	}

	updateDoneWontDoForCompleteByAny(node: ProseNode, oldData?: AssignableData) {
		const statusConditionChanged =
			// Status condition just changed
			node.attrs.statusCondition !== oldData?.node.attrs.statusCondition ||
			// Changed from multiple assigness to one or no assignee
			(node.attrs.assignees &&
				node.attrs.assignees.length <= 1 &&
				oldData &&
				oldData.node.attrs.assignees &&
				oldData.node.attrs.assignees.length > 1);
		if (node.attrs.done !== oldData?.node.attrs.done || statusConditionChanged) {
			this.assignableBlock.dataset.done = node.attrs.done;
		}
		if (
			node.attrs.wontDo !== oldData?.node.attrs.wontDo ||
			node.type.name !== oldData?.node.type.name ||
			statusConditionChanged
		) {
			this.assignableBlock.dataset.wontDo = node.attrs.wontDo && node.type.name === "todo";
		}
	}

	updateDoneWontDoForCompleteByAll(assignee: AssigneeStatusType, userId: string, oldData?: AssignableData) {
		const oldAssignee = oldData?.node.attrs.assignees?.find((assignee: AssigneeStatusType) => assignee.id === userId);
		// If we know that this couldn't have changed we just return
		if (
			oldAssignee &&
			assignee.status === oldAssignee.status &&
			oldData &&
			oldData.node.attrs.statusCondition === StatusCondition.ALL &&
			oldData.node.attrs.assignees &&
			oldData.node.attrs.assignees.length > 1
		) {
			return;
		}
		this.assignableBlock.dataset.done = (assignee.status === NoteBlockStatus.DONE).toString();
		this.assignableBlock.dataset.wontDo = (assignee.status === NoteBlockStatus.WONT_DO).toString();
	}

	update(data: AssignableData) {
		const oldData = this.data;
		this.data = data;

		const { node } = data;

		if (!node.attrs.statusCondition || node.attrs.statusCondition === StatusCondition.SINGLE) {
			this.updateDoneWontDoForCompleteByAny(node, oldData);
			// Complete by all but only 1 assignee (shouldn't happen but we are being safe) should use the any rule
		} else if (!node.attrs.assignees || node.attrs.assignees.length <= 1) {
			this.updateDoneWontDoForCompleteByAny(node, oldData);
		} else {
			// If the user is not an assignee we should still follow the complete by any
			const context = getContext(data.view.state);
			const userId = context?.userId;
			const assignee = node.attrs.assignees.find((assignee: AssigneeStatusType) => assignee.id === userId);
			if (!assignee) {
				this.updateDoneWontDoForCompleteByAny(node, oldData);
			} else {
				// Complete by all and the user is an assignee
				this.updateDoneWontDoForCompleteByAll(assignee, userId, oldData);
			}
		}
		if (node.attrs.originalBlockId !== oldData?.node.attrs.originalBlockId) {
			if (node.attrs.originalBlockId) {
				this.el.dataset.copied = "true";
			} else {
				delete this.el.dataset.copied;
			}
		}

		if (
			node.attrs.guidanceId &&
			node.attrs.guidanceId != oldData?.node.attrs.guidanceId &&
			!data.options.inQuotedItem
		) {
			if (this.guidance) this.guidance.el.remove();
			this.assignableBlock.dataset.suggestion = node.attrs.guidanceId;
			this.guidance = new Guidance(node.attrs.guidanceId, !!data.options.employeeGuidanceShowManager);
			this.checkboxContainer.insertAdjacentElement("afterend", this.guidance.el);
		} else if (!node.attrs.guidanceId && oldData?.node.attrs.guidanceId) {
			this.guidance?.el.remove();
			delete this.guidance;
			delete this.assignableBlock.dataset.suggestion;
		}

		this.assignableBlock.classList.toggle("moved", !!node.attrs.moved);

		// Only update the checkbox if it's not the first render.
		if (oldData) {
			this.checkbox.update(this.getCheckboxData(data));
			// We just rerender if the status condition changed or if it is a complete by all
			if (
				oldData.node.attrs.statusCondition !== data.node.attrs.statusCondition ||
				data.node.attrs.statusCondition === StatusCondition.ALL
			) {
				// TODO: There is probably a better way to do this
				this.checkboxContainer.innerHTML = "";
				this.checkboxContainer.append(this.checkbox.el);
			}
		}

		const linkedTextData = this.getLinkedTextData(data);
		if (linkedTextData) {
			if (this.linkedText) {
				this.linkedText.update(linkedTextData);
			} else {
				// ProseLinkedItemText will steal the pmcontent div, so grab the
				// container first.
				this.linkedText = new ProseLinkedItemText(linkedTextData);
				// Put it before the end; the link icon and jira stuff goes before the
				// prose content.
				this.assignableContent.replaceChild(this.linkedText.el, this.assignableInnerWrapper);
			}
		} else if (!linkedTextData && this.linkedText) {
			// No longer linked, unwrap the content from the linked text.
			this.assignableInnerWrapper.append(this.pmcontent);
			this.assignableContent.replaceChild(this.assignableInnerWrapper, this.linkedText.el);
			delete this.linkedText;
		}

		const linkIconData = this.getLinkIconData(data);
		if (!data.options.isPublic && linkIconData) {
			if (this.linkIcon) {
				this.linkIcon.update(linkIconData);
			} else {
				this.linkIcon = new LinkIcon(linkIconData);
				this.assignableContent.insertAdjacentElement("beforebegin", this.linkIcon.el);
			}
		} else if (this.linkIcon) {
			this.linkIcon.el.remove();
			this.linkIcon.deinit();
			delete this.linkIcon;
		}

		const jiraMetadataData = this.getJiraMetadataData(data);
		if (jiraMetadataData && !data.options.inQuotedItem) {
			if (this.jiraMetadata) {
				this.jiraMetadata.update(jiraMetadataData);
			} else {
				this.jiraMetadata = new JiraMetadata(jiraMetadataData);
				// If there's a link icon, this goes after that. Otherwise it's the
				// first thing in the assignable-content div.
				if (this.linkIcon) {
					this.linkIcon.el.insertAdjacentElement("afterend", this.jiraMetadata.el);
				} else {
					this.assignableContent.prepend(this.jiraMetadata.el);
				}
			}
		} else if (this.jiraMetadata) {
			this.jiraMetadata.el.remove();
			delete this.jiraMetadata;
		}

		// Don't need to update on first render, since these components are
		// initialized with the current data.
		if (oldData) {
			this.dueDateTrigger?.update({ node, view: data.view });
			if (AssigneeTrigger.shouldShow(node, data.view)) {
				this.assigneeTrigger.update({ node, view: data.view, viewerId: data.options.viewerId as string });
			}
		}

		const isEmpty = data.node.content.size === 0;
		if (this.blockActionMenu) {
			if (isEmpty) {
				this.blockActionMenu.deinit();
				this.blockActionMenu.el.remove();
				delete this.blockActionMenu;
			} else {
				this.blockActionMenu.update({
					node: data.node,
					view: data.view,
					triggers: this.getBlockActionTriggers(data),
					menuSections: this.getMenuItems,
				});
			}
		} else if (!isEmpty && blockActionsEnabled(data.view.state)) {
			this.blockActionMenu = new BlockActionMenu({
				node: data.node,
				view: data.view,
				triggers: this.getBlockActionTriggers(data),
				menuSections: this.getMenuItems,
			});
			this.el.appendChild(this.blockActionMenu.el);
		}
	}

	private getBlockActionTriggers(data: AssignableData): HTMLElement[] {
		if (!this.data) return [];
		const triggers = [];
		if (this.dueDateTrigger) {
			triggers.push(this.dueDateTrigger.el);
		}
		if (AssigneeTrigger.shouldShow(data.node, data.view)) {
			triggers.push(this.assigneeTrigger.el);
		}
		return triggers;
	}

	private getCheckboxData(data: AssignableData): NoteCheckboxData {
		const typeName = data.node.type.name;
		const { done, wontDo, originalBlockId, statusCondition, assignees } = data.node.attrs;
		let state: "checked" | "unchecked" | "indeterminate" = done
			? "checked"
			: wontDo && typeName === "todo"
				? "indeterminate"
				: "unchecked";

		let isUserAssignee = false;
		let assigneesData: Array<AssigneeCompletionData> = assignees;
		const context = getContext(data.view.state);
		if (typeName === "todo" && statusCondition === StatusCondition.ALL && assignees.length > 1) {
			const user = context?.userId;
			const userAssignee = assignees.find((assignee: AssigneeStatusType) => assignee.id === user);
			if (userAssignee) {
				isUserAssignee = true;
				state =
					userAssignee.status === NoteBlockStatus.DONE
						? "checked"
						: userAssignee.status === NoteBlockStatus.WONT_DO
							? "indeterminate"
							: "unchecked";
			}

			// Fill user attrs into assignees so we don't need to lazy load it
			const participantUsersMap = new Map<string, AttendeeAttrs>();
			context?.participants?.forEach((attendee: AttendeeAttrs) => {
				const userId = attendee.id || attendee.user?.id || "";
				if (!userId) return;
				participantUsersMap.set(userId, attendee);
			});
			assigneesData = assigneesData.map(assignee => {
				if (!participantUsersMap.has(assignee.id)) return assignee;
				return {
					...assignee,
					attendeeAttrs: participantUsersMap.get(assignee.id),
				};
			});
		}

		return {
			type:
				typeName === "todo"
					? data.node.attrs.aiGenerated
						? "generated-action-item"
						: "action-item"
					: typeName === "agenda"
						? "talking-point"
						: "goal",
			state,
			statusCondition,
			assignees: assigneesData,
			isUserAssignee,
			disabled: data.view.props.editable?.(data.view.state) === false && !!data.options.disabledReadOnly,
			isSnippet: getContext(data.view.state)?.isSnippet ?? false,
			linked: !!originalBlockId,
			onChange: this.onChange,
			onToggleAssignee: this.onToggleAssignee,
			onAssignYourself: this.onAssignYourself,
			className: styles.checkbox,
		};
	}

	private onChange = (checked: boolean): void => {
		if (!this.data) return;
		const toggleField = this.data.options.toggleField as (field: "DONE" | "WONT_DO", value: boolean) => void;
		toggleField("DONE", checked);
	};

	private onToggleAssignee = (assignee: AssigneeCompletionData): void => {
		if (!this.data) return;
		const { view, node, getPos } = this.data;
		if (typeof getPos !== "function") return;
		const { assignees, done } = node.attrs;
		let doneStatusAmount = 0;
		const newAssignees = assignees
			? assignees.map((currentAssignee: AssigneeStatusType) => {
					if (currentAssignee.id !== assignee.id) {
						if (currentAssignee.status === NoteBlockStatus.DONE) {
							doneStatusAmount++;
						}
						return { ...currentAssignee };
					}
					// We are toggling to done so we sum to the total
					if (currentAssignee.status === NoteBlockStatus.NOT_DONE) {
						doneStatusAmount++;
					}
					return {
						...currentAssignee,
						status:
							currentAssignee.status === NoteBlockStatus.NOT_DONE ? NoteBlockStatus.DONE : NoteBlockStatus.NOT_DONE,
					};
				})
			: [];
		let attrs: { assignees: Array<AssigneeStatusType>; done?: boolean; aiGenerated?: boolean } = {
			assignees: newAssignees,
		};
		const newDone = doneStatusAmount === newAssignees.length;
		if (newDone !== done) {
			attrs.done = newDone;
		}
		// If the item has been marked done for any assignee, it should be accepted
		if (isAcceptFlowFFOn() && doneStatusAmount > 0 && node.attrs.aiGenerated) {
			attrs.aiGenerated = false;
		}
		setBlockType(node.type, attrs, NodeSelection.create(view.state.doc, getPos()))(view.state, view.dispatch);
	};

	private onAssignYourself = (): void => {
		if (!this.data) return;
		const context = getContext(this.data.view.state);
		const userId = context?.userId;
		if (!userId) return;
		const { view, node, getPos } = this.data;
		if (typeof getPos !== "function") return;
		const { assignees } = node.attrs;
		const newAssignees = [
			...assignees,
			{
				id: userId,
				status: NoteBlockStatus.NOT_DONE,
			},
		];
		const attrs = {
			assignees: newAssignees,
			done: false,
			wontDo: false,
		};
		setBlockType(node.type, attrs, NodeSelection.create(view.state.doc, getPos()))(view.state, view.dispatch);
	};

	private getLinkedTextData(data: AssignableData): ProseLinkedItemTextData | null {
		const { originalBlockId } = data.node.attrs;
		if (!originalBlockId) return null;

		const context = getContext(data.view.state);

		return {
			originalBlockId,
			disablePopup: !data.options.enableCopiedItemPopup,
			disableClick: !!data.options.inQuotedItem,
			isTeams: context?.isTeams ?? false,
			hasAccessToOriginalNote: this.hasAccessToOriginalNote(data),
			originatingNoteId: (data.options.note as { id?: string } | undefined)?.id ?? context?.noteId ?? "",
			content: this.pmcontent,
		};
	}

	private getLinkIconData(data: AssignableData): LinkIconData | null {
		if (!(data.node.attrs.originalBlockId || data.decorations.some(d => d.spec.linked))) {
			return null;
		}
		return {
			className: assignableStyles.actionItemsLinkage,
			blockKey: data.node.attrs.blockId,
			noteId: getContext(data.view.state)?.noteId,
			isOriginal: !data.node.attrs.originalBlockId,
			onClick: () => blurView(data.view),
			disableClick: !!data.options.inQuotedItem,
		};
	}

	private getUser(): AttendeeAttrs | undefined {
		const context = this.data && getContext(this.data.view.state);
		return context?.participants?.find((u: AttendeeAttrs) => u.id === context?.userId);
	}

	private getJiraMetadataData(data: AssignableData): JiraMetadataData | null {
		const { node, view, options } = data;
		const context = getContext(view.state);

		if (
			!context ||
			options.disableJira ||
			!node.attrs.linkedIssue ||
			!context.jira ||
			this.getUser()?.isGuest ||
			(hasOwnProperty(node.attrs.linkedIssue, "jiraConnectionID") &&
				node.attrs.linkedIssue.jiraConnectionID !== context.jira.id)
		) {
			return null;
		}

		const { key, statusName, statusColor } = node.attrs.linkedIssue;
		return { key, statusName, statusColor, view };
	}

	private getMenuItems: BlockActionMenuData["menuSections"] = close => {
		if (!this.data || typeof this.data.getPos === "boolean") {
			return [];
		}

		const { node, view, getPos } = this.data;
		const context = getContext(view.state);
		const { assignees, blockId, creator, done, dueDate, originalBlockId, wontDo, statusCondition, completedBy } =
			node.attrs;

		const itemHasActiveIntegration = Object.values(
			activeIntegrationLinks({
				state: view.state,
				blockKey: blockId,
				position: getPos(),
			}),
		).includes(true);

		const activeParticipants = context.participants.filter((p: AttendeeAttrs) => p.user?.isActive || p.invitable);

		const menuProps = {
			includeCopyTo: true,
			hasAccessToDuedates: context?.billing?.hasAccessToDuedates || false,
			hasAccessToJiraIntegration: context?.billing?.hasAccessToJiraIntegration || false,
			hasAccessToAsana: context?.billing?.hasAccessToAsana || false,
			hasAccessToMultipleAssignees: context?.billing?.hasAccessToMultipleAssignees || false,
			connectedActionItemIntegrations: context?.connectedActionItemIntegrations || [],
			itemHasActiveIntegration,
			billingFeatureAccess: context?.billing?.featureAccess,
			isAccountAdmin: context?.isAccountAdmin || false,
			isGuest: context?.isGuest || false,
			isTeams: context?.isTeams || false,
			dueDate,
			creatorId: creator,
			assigneeId: assignees.length > 0 ? assignees[0].id : null,
			assignees: assignees,
			statusCondition: statusCondition,
			nodeType: node.type,
			setBlockAttrs: (values: ProseNode["attrs"]) => {
				if (!this.data) return false;
				if (typeof this.data.getPos === "boolean") return false;
				const pos = this.data.getPos();
				return setBlockType(node.type, values, NodeSelection.create(view.state.doc, pos))(view.state, view.dispatch);
			},
			participants: activeParticipants,
			noteId: context?.noteId,
			blockKey: blockId,
			user: this.getUser(),
			jira: context?.jira,
			originalBlockId,
			hasAccessToOriginalNote: this.hasAccessToOriginalNote(this.data),
			done,
			streamId: context?.streamId,
			streamName: context?.streamName,
			streamType: context?.streamType,
			streamIsExternal: context?.streamIsExternal,
			streamHasExternalUsers: context?.streamHasExternalUsers,
			isTodoStream: false,
			eventGuid: context?.noteEventGuid,
			reRenderEditor: () => {
				if (!this.data) return;
				const { view } = this.data;
				// @ts-ignore
				view.docView.dirty = 3;
				view.update(view.props);
			},
			afterInvite: context?.refetchNote,
			allowCompleteByAny: context?.allowCompleteByAny,
			completedBy: completedBy ? this.findContributor(completedBy) : null,
			editorView: view,
			getPos,
			aiGenerated: Boolean(node.attrs.aiGenerated),
			// If an action item was suggested by AI and then accepted, aiGenerated will be false but it will still have
			// the recording attr, which is how we can determine it was AI-suggested originally
			originallyAiGenerated: Boolean(node.attrs.recording),
		};
		if (context?.isCustomSection) {
			return getCustomSectionTips()(close);
		}

		if (node.type.name === "todo") {
			return getActionItemMenu({
				...menuProps,
				wontDo,
				view,
			})(close);
		} else {
			return getItemsForProse({
				...menuProps,
				includeCopyTo: node.type.name === "agenda",
				nodeType: node.type.name,
			})(close);
		}
	};

	private hasAccessToOriginalNote(data: AssignableData) {
		const { view, node } = data;
		const { originalBlockId, creator } = node.attrs;
		const context = getContext(view.state);
		const user = this.getUser();
		const hasAccessToOriginalNote = originalBlockId
			? creator === user?.id || !!context?.hasAccessToOriginalBlock?.[originalBlockId]
			: true;
		return hasAccessToOriginalNote;
	}

	private findContributor(id: string) {
		const context = this.data && getContext(this.data.view.state);
		return context?.participants?.find((u: AttendeeAttrs) => u.id === id);
	}
}

function toggleField(noteId: string, node: ProseNode, field: "DONE" | "WONT_DO", value: boolean): void {
	commitMutation<MutationType>(environment, {
		mutation: Assignable.toggleBlockFieldMutation,
		variables: {
			input: {
				field,
				noteId,
				noteBlockKey: node.attrs.blockId,
				value,
			},
		},
	});
}

class AssignableView implements NodeView {
	component: AssignableComponent;
	dom: HTMLElement;
	contentDOM: HTMLElement;

	constructor(
		public node: ProseNode,
		public view: EditorView,
		public getPos: getPosHandler,
		decorations: readonly Decoration[],
		public options: { [k: string]: unknown },
	) {
		this.options = { ...options, toggleField: this.toggleField };
		this.component = new AssignableComponent({
			node,
			view,
			getPos,
			decorations,
			options: this.options,
		});
		this.dom = this.component.el;
		this.contentDOM = this.component.contentDOM;
	}

	private toggleField = (field: "DONE" | "WONT_DO", value: boolean) => {
		if (this.options.localToggle) {
			const { view, node, getPos } = this;
			if (typeof getPos !== "function") return;
			const newAttrs: { [key: string]: boolean } = {};
			if (field === "DONE") {
				newAttrs["done"] = value;
			} else if (field === "WONT_DO") {
				newAttrs["wontDo"] = value;
			}
			setBlockType(node.type, newAttrs, NodeSelection.create(view.state.doc, getPos()))(view.state, view.dispatch);
			return;
		}

		const { statusCondition, assignees } = this.node.attrs;
		if (
			this.node.type.name === "todo" &&
			statusCondition === StatusCondition.ALL &&
			assignees &&
			assignees.length > 1
		) {
			const context = getContext(this.view.state);
			const user = context?.participants?.find((u: AttendeeAttrs) => u.id === context?.userId);
			const userAssignee = assignees.find((assignee: AssigneeStatusType) => assignee.id === user?.id);
			if (!userAssignee) {
				// Open completion menu
				return;
			}
			const { view, node, getPos } = this;
			if (typeof getPos !== "function") return;
			let doneStatusAmount = 0;
			const newAssignees = assignees.map((assignee: AssigneeStatusType) => {
				if (assignee.id !== user?.id) {
					doneStatusAmount += assignee.status === NoteBlockStatus.NOT_DONE ? 0 : 1;
					return { ...assignee };
				}
				let newStatus = NoteBlockStatus.NOT_DONE;
				if (field === "DONE" && value) {
					newStatus = NoteBlockStatus.DONE;
					doneStatusAmount += 1;
				} else if (field === "WONT_DO" && value) {
					newStatus = NoteBlockStatus.WONT_DO;
					doneStatusAmount += 1;
				}
				return {
					...assignee,
					status: newStatus,
				};
			});
			const newDone = doneStatusAmount === newAssignees.length;
			const attrsToUpdate: { assignees: Array<AssigneeStatusType>; done?: boolean; aiGenerated?: boolean } = {
				assignees: newAssignees,
			};
			if (newDone !== node.attrs.done) {
				attrsToUpdate.done = newDone;
			}

			if (isAcceptFlowFFOn() && field === "DONE" && value && node.attrs.aiGenerated) {
				attrsToUpdate.aiGenerated = false;
			}
			setBlockType(node.type, attrsToUpdate, NodeSelection.create(view.state.doc, getPos()))(view.state, view.dispatch);
			return;
		}

		const noteId = (this.options.note as { id: string } | undefined)?.id;
		if (!noteId) return;
		toggleField(noteId, this.node, field, value);
	};

	update(newNode: ProseNode, decorations: readonly Decoration[]) {
		// If we're given a different block or the block changes type, we return
		// false which should have ProseMirror recreate the nodeview (in all
		// likelihood this nodeview is not the correct one for the new node type).
		if (
			newNode.attrs.blockId !== this.node.attrs.blockId ||
			newNode.type !== this.node.type ||
			// If the node becomes linked or becomes unlinked, it should switch to
			// the other node view (either this one or CopiedAssignableView) since
			// they have different ignoreMutation and setSelection methods.
			(newNode.attrs.originalBlockId === null) !== (this.node.attrs.originalBlockId === null)
		) {
			return false;
		}
		this.component.update({
			node: newNode,
			view: this.view,
			getPos: this.getPos,
			decorations,
			options: this.options,
		});
		this.node = newNode;
		return true;
	}

	destroy() {
		this.component.deinit();
	}

	ignoreMutation(record: MutationRecord | { type: "selection"; target: Element }): boolean {
		if (
			record.type === "selection" &&
			(record.target === this.contentDOM ||
				record.target.classList.contains(goalCheckboxStyles.svg) ||
				record.target.classList.contains(noteCheckboxStyles.checkboxSvg) ||
				record.target.classList.contains("triggers"))
		) {
			return false;
		}
		if (
			record.type === "childList" &&
			record.target instanceof HTMLElement &&
			(record.target.classList.contains("assignable-inner-wrapper") || record.target.hasAttribute("pmcontent"))
		) {
			return false;
		}
		if (record.type !== "selection" && !this.contentDOM?.contains(record.target)) {
			return true;
		}
		if (record.type == "characterData" || record.type == "childList") {
			return false;
		}
		return true;
	}
}

class CopiedAssignableView extends AssignableView {
	ignoreMutation(record: MutationRecord | { type: "selection"; target: Element }): boolean {
		return record.type !== "selection";
	}

	setSelection(_anchor: number, _head: number, root: Document | ShadowRoot): void {
		if (!this.contentDOM) return;
		if (root instanceof ShadowRoot) return;
		let selection = root.getSelection();
		if (selection) {
			selection.empty();
		}
		const doc = this.view.state.doc;
		let blockStart = 0;
		for (let i = 0; i < doc.childCount; i++) {
			const child = doc.child(i);
			if (child.attrs.blockId === this.node.attrs.blockId) {
				break;
			}
			blockStart = blockStart + child.nodeSize;
		}
		this.view.dispatch(this.view.state.tr.setSelection(NodeSelection.create(this.view.state.doc, blockStart)));
		selection = root.getSelection();
		if (selection) {
			selection.empty();
		}
	}
}

function handleBackspaceIntoLinkedTodo(state: EditorState, _dispatch?: (tr: Transaction) => void): boolean {
	const { $from, empty } = state.selection,
		isSelectionAtStart = $from.parentOffset === 0,
		blockHasContent = $from.depth !== 0 && !!$from.node(1).content.size;

	if (!empty || !isSelectionAtStart || !blockHasContent) return false;

	const blockBefore = state.doc.resolve($from.before(1)).nodeBefore;
	if (!blockBefore || blockBefore.type.name !== "todo" || !blockBefore.attrs.originalBlockId) return false;

	// By now we know that the cursor is at the start of the block, there is
	// content after the cursor, and the previous block is a linked action item.
	// We return true here to prevent any action from happening, since if we
	// allowed it the text in this block would be joined to the text of the
	// linked action item (which should not be editable).
	return true;
}

abstract class Assignable extends NodeExtension {
	static toggleBlockFieldMutation = graphql`
		mutation assignableToggleFieldDoneMutation($input: ToggleBlockFieldInput!) {
			toggleBlockField(input: $input) {
				ok
			}
		}
	`;

	view = (node: ProseNode, view: EditorView, getPos: getPosHandler, decorations: readonly Decoration[]): NodeView => {
		if (node.attrs.originalBlockId) {
			return new CopiedAssignableView(node, view, getPos, decorations, this.options);
		} else {
			return new AssignableView(node, view, getPos, decorations, this.options);
		}
	};

	get node() {
		const { name } = this;

		return {
			content: "inline*",
			group: "block",
			defining: true,
			attrs: {
				blockId: { default: null },
				indent: { default: 0 },
				done: { default: false },
				wontDo: { default: false },
				assignees: { default: [] },
				creator: { default: null },
				placeholder: { default: "" },
				moved: { default: false },
				originalBlockId: { default: null },
				dueDate: { default: null },
				linkedIssue: { default: null },
				statusCondition: { default: StatusCondition.SINGLE },
				completedBy: { default: null },
				...promptResponseAttrs,
			},
			parseDOM: [
				{
					tag: `[data-block-type="${name}"]`,
					getAttrs: (dom: Node | string) => {
						let assignees: Array<AssigneeStatusType> = [];
						const assigneesData = dom instanceof Element ? dom.getAttribute("data-assignees")?.split(",") : undefined;
						if (assigneesData && assigneesData.length > 0) {
							assigneesData.forEach(assigneeData => {
								const splittedAssigneeData = assigneeData.split(":");
								if (splittedAssigneeData.length < 2) return;
								assignees.push({ id: splittedAssigneeData[0], status: splittedAssigneeData[1] as NoteBlockStatus });
							});
						}
						return dom instanceof Element
							? {
									done: dom.getAttribute("data-done") === "true",
									wontDo: dom.getAttribute("data-wont-do") === "true",
									assignees,
									indent: parseInt(dom.getAttribute("data-indent") || "0", 10),
									creator: dom.getAttribute("data-creator"),
									dueDate: dom.getAttribute("data-duedate") || null,
									linkedIssue: JSON.parse(dom.getAttribute("data-linked-issue") || "null"),
									statusCondition: dom.getAttribute("data-status-condition"),
									completedBy: dom.getAttribute("data-completed-by"),
								}
							: null;
					},
				},
			],
			toDOM(node: ProseNode): DOMOutputSpec {
				const { done, wontDo, assignees, indent, placeholder, dueDate, linkedIssue, statusCondition, completedBy } =
					node.attrs;

				return [
					"div",
					{
						"data-block-id": node.attrs["blockId"],
						"data-block-type": node.type.name,
						"data-done": done?.toString() || "false",
						"data-wont-do": wontDo.toString(),
						"data-assignees": assignees
							.map((assignee: { [key: string]: string }) => `${assignee.id}:${assignee.status}`)
							.toString(),
						"data-creator": node.attrs.creator,
						"data-indent": indent.toString(),
						"data-placeholder": placeholder,
						"data-duedate": dueDate,
						"data-linked-issue": JSON.stringify(linkedIssue),
						"data-status-condition": statusCondition,
						"data-completed-by": completedBy,
					},
					0,
				];
			},
		};
	}

	handleKey = (state: EditorState, dispatch: ((tr: Transaction) => void) | undefined) => {
		const { selection } = state;
		if (selection.empty) {
			const { $anchor } = selection;
			const { parent: node } = $anchor;
			if (node.type.name === this.name) {
				if (this.options.note) {
					const { statusCondition, assignees } = node.attrs;
					if (node.type.name === "todo" && statusCondition === StatusCondition.ALL) {
						const nodeAndPos = findBlockAndPos(state.doc, node.attrs.blockId);
						if (!nodeAndPos) return false;
						const [_node, pos] = nodeAndPos;
						const context = getContext(state);
						const user = context?.participants?.find((u: AttendeeAttrs) => u.id === context?.userId);
						const userAssignee = assignees.find((assignee: AssigneeStatusType) => assignee.id === user?.id);
						if (!userAssignee) {
							// TODO: Open completion menu
							return false;
						}
						const newAssignees = assignees.map((assignee: AssigneeStatusType) => {
							if (assignee.id !== user?.id) return { ...assignee };
							return {
								...assignee,
								status: assignee.status === NoteBlockStatus.NOT_DONE ? NoteBlockStatus.DONE : NoteBlockStatus.NOT_DONE,
							};
						});
						setBlockType(node.type, { assignees: newAssignees }, NodeSelection.create(state.doc, pos))(state, dispatch);
						return true;
					}

					toggleField(
						this.options.note.id,
						node,
						"DONE",
						!(node.attrs.done || (node.attrs.wontDo && node.type.name === "todo") || false),
					);
				}
				return true;
			}
		}
		return false;
	};

	keys(): Keybinding | null {
		return {
			"Mod-Enter": this.handleKey,
			Backspace: handleBackspaceIntoLinkedTodo,
		};
	}
}

export class Agenda extends Assignable {
	get name() {
		return "agenda";
	}

	get node() {
		const node = super.node;
		return { ...node, attrs: { ...node.attrs, guidanceId: { default: null } } };
	}

	menuEntry() {
		return {
			icon: faCheckCircle,
			title: "Talking point",
			subtitle: "Add a discussion item.",
			run: () => {
				return setBlockType(this.$type);
			},
			section: "types" as const,
			priority: 7,
			keyboardShortcut: "()",
		};
	}

	inputRules() {
		return [
			blockTypeShortcut("\\(\\)", this.$type, undefined, {
				trailingWhitespace: false,
				predicate: node => node.type.name !== this.$type.name,
			}),
		];
	}
}

export class Todo extends Assignable {
	private maxLengthErrorToastId?: ToastId;

	get name() {
		return "todo";
	}

	menuEntry() {
		return {
			icon: faCheckSquare,
			title: "Action item",
			subtitle: "Add, assign, schedule tasks.",
			run: () => {
				return setBlockType(this.$type);
			},
			section: "types" as const,
			priority: 6,
			keyboardShortcut: "[]",
		};
	}

	handleReturnInJiraIntegratedIssues = (state: EditorState, dispatch: ((tr: Transaction) => void) | undefined) => {
		const { selection } = state;
		const { $anchor } = selection;
		const { parent: node } = $anchor;
		if (node.type.name === this.name) {
			const { $from, $to } = state.selection;

			const attrsWithoutLinkedIssue = (() => {
				const attrs = { ...$from.node().attrs };
				if (hasOwnProperty(attrs, "linkedIssue")) {
					delete attrs.linkedIssue;
				}
				if (hasOwnProperty(attrs, "indent") && attrs.indent == undefined) {
					delete attrs.indent;
				}
				return attrs;
			})();

			if ($from.node(-1).type.name !== "doc") return false; // only do this in doc's direct children
			if (dispatch) {
				let tr = state.tr;
				if (!selection.empty) {
					tr = tr.deleteSelection();
				}
				tr = tr.split($from.pos);
				tr = tr.setSelection(TextSelection.create(tr.doc, tr.mapping.map($to.pos)));
				const [blockStart] = expandSelectionToBlocks(tr.mapping.map($to.pos), tr.mapping.map($to.pos), tr.doc);
				tr = tr.setNodeMarkup(blockStart, node.type, attrsWithoutLinkedIssue);
				dispatch(tr);
			}
			return true;
		}
		return false;
	};

	get node() {
		return {
			...super.node,
			attrs: { ...super.node.attrs, aiGenerated: { default: false } },
			parseDOM: [
				{
					...super.node.parseDOM[0],
					getAttrs: (dom: Node | string) => {
						const attrs = super.node.parseDOM[0].getAttrs(dom);
						if (attrs === null || !(dom instanceof Element)) return null;
						return { ...attrs, aiGenerated: dom.getAttribute("data-ai-generated") === "true" };
					},
				},
			],
			toDOM: (node: ProseNode): DOMOutputSpec => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const outputSpec = super.node.toDOM(node) as [string, ...any];

				// The type cast above can be done with confidence as long as `Assignable`'s node.toDOM() returns
				// an array DOMOutputSpec (i.e. `[string, ...any]` instead of `string` or `Node`).
				// Its size can alo be confidently assumed for the same reason.

				return [
					outputSpec[0],
					{
						...outputSpec[1],
						"data-ai-generated": node.attrs.aiGenerated.toString(),
					},
					...outputSpec.slice(2),
				];
			},
		};
	}

	get plugins() {
		return [
			new Plugin({
				props: {
					/**
					 * Handle text keyboard input.
					 *
					 * @param {EditorView} view - The view that generated the key event.
					 * @return {boolean} True if text should be suppressed, otherwise false.
					 */
					handleTextInput: (view: EditorView) => {
						const selection = view.state.selection;

						const range = selection.$from.blockRange(selection.$to);
						if (!range) return false;

						const { jira } = getContext(view.state);
						if (!jira) return false;

						function checkIsJiraText(depth: number, pos: ResolvedPos): boolean {
							if (pos.node(depth).attrs.linkedIssue) {
								return true;
							}
							if (depth > 0) {
								return checkIsJiraText(depth - 1, pos);
							}
							return false;
						}

						const isJiraText =
							checkIsJiraText(selection.$from.depth, selection.$from) ||
							checkIsJiraText(selection.$to.depth, selection.$to);

						if (!isJiraText) {
							return false;
						}

						const [start] = expandSelectionToBlocks(selection.from, selection.to, view.state.doc);
						const nodeSelection = NodeSelection.create(view.state.doc, start);

						let initialText: string = selectionToJiraText(nodeSelection);

						if (initialText.length > 255) {
							if (!this.maxLengthErrorToastId || !toast.isActive(this.maxLengthErrorToastId)) {
								this.maxLengthErrorToastId = toast.error(
									"Cannot put more than 255 characters into an action item linked to Jira.",
								);
							}
							return true;
						}

						return false;
					},
				},
			}),
		];
	}

	keys() {
		return {
			"Mod-Enter": this.handleKey,
			Enter: this.handleReturnInJiraIntegratedIssues,
		};
	}

	inputRules() {
		return [
			blockTypeShortcut("\\[]", this.$type, undefined, {
				trailingWhitespace: false,
				predicate: node => node.type.name !== this.$type.name,
			}),
		];
	}
}

export class Goal extends Assignable {
	get name() {
		return "goal";
	}
}

export const ASSIGNABLE_TYPE_NAMES: readonly string[] = ["todo", "agenda", "goal"];
